import { useTranslation } from 'react-i18next';
import { VERANTWOORDING_ANKER, useMeldVerantwoording } from './VerantwoordingAnker';
import { effectieveDrempel } from '../drempel';
import { formatGetal } from '../format';
import type { Verantwoording } from '../work-quality.api';

export interface AccountabilityFooterProps {
  /**
   * Wat er onder de cijfers hoort te staan — de drempel, en niet meer dan dat.
   *
   * `Verantwoording` en niet `Afbakening`: het overzicht van alle medewerkers draagt geen
   * `uitgeslotenVelden` (`MedewerkerslijstAfbakening` in `presentatie.ts`).
   */
  afbakening: Verantwoording;
  /** Het niveau van de visueel verborgen kop; 3 binnen het Zo-ziet-kader (story 9, 11). */
  kopNiveau?: 2 | 3 | 4;
}

/**
 * Stap 5 van de leesvolgorde: de verantwoordingsvoet (meetregel 7, UX-DR3).
 *
 * Één zin, altijd zichtbaar. Geen accordeon, geen tooltip, geen info-icoon: de drempel is de
 * voorwaarde waaronder de cijfers iets betekenen, en wat je moet openklappen lees je niet.
 *
 * **De voet staat op het vlak van de kaarten erboven en niet op de paginakleur.** Gemeten op
 * 4 september 2026 in een echte browser: `--foreground-muted` op `--app-bg` haalt bij 12px een
 * contrast van 4,20:1, en WCAG 2.2 AA vraagt er 4,5. Op `--background` is dat 4,76 en klopt het.
 */
export function AccountabilityFooter({ afbakening, kopNiveau = 2 }: AccountabilityFooterProps) {
  const { t } = useTranslation();
  const Kop = `h${kopNiveau}` as const;
  // Zolang deze voet er staat, mag de schil de skip-link ernaartoe tekenen.
  useMeldVerantwoording();

  return (
    <section data-werkkwaliteit-sectie="verantwoording">
      <div
        className="border-t border-[var(--border-soft)] bg-[var(--background)] px-5 py-3.5 text-[0.75rem] text-[var(--foreground-muted)]"
        data-testid="werkkwaliteit-verantwoording"
        id={VERANTWOORDING_ANKER}
        // Zonder dit springt de pagina wel, maar blijft de focus bij de link achter — en dan loopt
        // wie met het toetsenbord werkt de tabellen alsnog door.
        tabIndex={-1}
      >
        <Kop className="sr-only">{t('qa.eigen.verantwoordingKop')}</Kop>
        <p>
          {/* Uitzondering D-4 (Friso 2026-11-04): de feedbackrapporten zijn alleen Nederlands, ook bij een Engelse weergave. */}
          {t('qa.eigen.verantwoordingZin', {
            lng: 'nl',
            drempel: formatGetal(effectieveDrempel(afbakening.drempelProductenPerPersoon), 'nl'),
          })}
        </p>
      </div>
    </section>
  );
}
